import { Checkbox, makeStyles, tokens, Tooltip } from "@fluentui/react-components";
import React from "react";
import { NewsResponse } from "../../../libs/services/NewsService";

const useClasses = makeStyles({
    newsItemContent: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        cursor: 'pointer',
        outline: 'none',
        '&:focus-visible': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        }
    },
    newsDetails: {
        flex: 1
    },
    newsTitle: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
        cursor: 'inherit',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        hyphens: 'auto',
        maxWidth: "180px",
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    newsDate: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        cursor: 'default',
    },
    editedLabel: {
        fontSize: tokens.fontSizeBase200,
        fontStyle: 'italic',
        color: tokens.colorNeutralForeground3,
        marginLeft: '6px',
    },
});

interface NewsFlags {
    edited?: boolean;
}

interface NewsListItemProps {
    item: NewsResponse & { flags?: NewsFlags };
    selectedNewsIds: string[];
    handleCheckboxChange: (newsId: string, checked: boolean) => void;
    onSelectNews: (id: string) => void;
    isSelected: boolean;
}

export const NewsListItem: React.FC<NewsListItemProps> = ({
    item,
    selectedNewsIds,
    handleCheckboxChange,
    onSelectNews,
    isSelected
}) => {
    const classes = useClasses();

    const formatDate = (dateInput: string | Date): string => {
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(date.getTime())) return "Kein Datum";
        return new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }).format(date);
    };

    const handleActivate = () => {
        onSelectNews(item.id);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleActivate();
        }
    };

    return (
        <div
            className={classes.newsItemContent}
            tabIndex={0}
            role="button"
            aria-label={`News bearbeiten: ${item.title}`}
            onClick={handleActivate}
            onKeyDown={handleKeyDown}
            data-selected={isSelected}
        >
            <Checkbox
                checked={selectedNewsIds.includes(item.id)}
                onChange={(_, data) => { handleCheckboxChange(item.id, data.checked === true); }}
                onClick={(e) => { e.stopPropagation(); }}
            />
            <div className={classes.newsDetails}>
                <div className={classes.newsTitle}>
                    <Tooltip content={item.title} relationship="label" withArrow>
                        <span>{item.title}</span>
                    </Tooltip>
                </div>
                <div className={classes.newsDate}>
                    {formatDate(item.createdAt)}
                    {item.flags.edited && (
                        <Tooltip content="Diese News wurde bearbeitet" relationship="label" withArrow>
                            <span className={classes.editedLabel}>(Bearbeitet)</span>
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};
